"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import type { FUSportsLivescoreEvent, SearchEntityResult } from "fansunited-data-layer";
import { cn } from "@/utilities/core/cn";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { MatchTimeColumn } from "./MatchTimeColumn";
import { getBreakdownPeriods } from "./score-helpers";
import { asCompetitor, getResultScore } from "@/views/livescore/utils/livescore";
const DEFAULT_BEST_OF_SETS = 5;
interface TennisMatchRowProps {
  event: FUSportsLivescoreEvent;
  entities: Record<string, SearchEntityResult>;
}
interface TennisCompetitorDisplayProps {
  name: string;
  flag?: string | null;
  isWinner?: boolean;
  isLoser?: boolean;
}
function TennisCompetitorDisplay(t0) {
  const $ = _c(8);
  const {
    name,
    flag,
    isWinner,
    isLoser
  } = t0;
  const nameClass = isWinner ? "font-semibold" : isLoser ? "text-muted-foreground" : "font-medium";
  let t1;
  if ($[0] !== nameClass) {
    t1 = cn("flex items-center gap-1.5 flex-1 text-sm truncate", nameClass);
    $[0] = nameClass;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== flag) {
    t2 = flag && <img src={flag} alt="" className="w-3.5 h-3.5 object-contain shrink-0" />;
    $[2] = flag;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== name || $[5] !== t1 || $[6] !== t2) {
    t3 = <span className={t1}>{t2}{name}</span>;
    $[4] = name;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
export function TennisMatchRow(t0) {
  const $ = _c(75);
  const {
    event,
    entities
  } = t0;
  const labels = useLabels();
  let t1;
  if ($[0] !== event.status?.type) {
    t1 = event.status?.type?.toUpperCase();
    $[0] = event.status?.type;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const statusType = t1;
  const isLive = statusType === "LIVE";
  const isFinished = statusType === "FINISHED";
  const isActive = isLive || isFinished;
  let t2;
  if ($[2] !== entities || $[3] !== event.competitorOne.id) {
    t2 = asCompetitor(entities, event.competitorOne.id);
    $[2] = entities;
    $[3] = event.competitorOne.id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const playerOne = t2;
  let t3;
  if ($[5] !== entities || $[6] !== event.competitorTwo.id) {
    t3 = asCompetitor(entities, event.competitorTwo.id);
    $[5] = entities;
    $[6] = event.competitorTwo.id;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const playerTwo = t3;
  const nameOne = playerOne?.name ?? event.competitorOne.id;
  const nameTwo = playerTwo?.name ?? event.competitorTwo.id;
  let setsWonC2;
  let t10;
  let t11;
  let t12;
  let t13;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[8] !== event || $[9] !== isActive || $[10] !== isFinished || $[11] !== labels.livescore.set || $[12] !== nameOne || $[13] !== nameTwo || $[14] !== playerOne?.assets?.logo || $[15] !== playerOne?.assets?.photo || $[16] !== playerOne?.country?.code || $[17] !== playerOne?.country?.flag || $[18] !== playerTwo?.assets?.logo || $[19] !== playerTwo?.assets?.photo || $[20] !== playerTwo?.country?.code || $[21] !== playerTwo?.country?.flag) {
    const sets = getBreakdownPeriods(event.score, labels.livescore.set, DEFAULT_BEST_OF_SETS);
    const derivedSetsWonC1 = sets.filter(_temp).length;
    const derivedSetsWonC2 = sets.filter(_temp2).length;
    let t14;
    if ($[33] !== event.score) {
      t14 = getResultScore(event.score);
      $[33] = event.score;
      $[34] = t14;
    } else {
      t14 = $[34];
    }
    const aggregate = t14;
    const aggregateC1 = Number(aggregate.competitorOne);
    const aggregateC2 = Number(aggregate.competitorTwo);
    const hasSetBreakdown = sets.length > 0;
    const setsWonC1 = hasSetBreakdown ? derivedSetsWonC1 : Number.isFinite(aggregateC1) ? aggregateC1 : 0;
    setsWonC2 = hasSetBreakdown ? derivedSetsWonC2 : Number.isFinite(aggregateC2) ? aggregateC2 : 0;
    const c1Won = isFinished && setsWonC1 > setsWonC2;
    const c2Won = isFinished && setsWonC2 > setsWonC1;
    const setScoreClass = _temp3;
    const totalScoreClass = _temp4;
    t12 = "flex items-stretch";
    if ($[35] !== event) {
      t13 = <div className="w-16 shrink-0 flex items-center justify-center px-1 py-3.5"><MatchTimeColumn event={event} sport="tennis" /></div>;
      $[35] = event;
      $[36] = t13;
    } else {
      t13 = $[36];
    }
    t9 = "flex-1 py-3 min-w-0";
    t10 = isActive && sets.length > 0 && <div className="flex items-center gap-2 text-[10px] text-muted-foreground mb-0.5 pr-1"><div className="w-8 shrink-0" /><div className="flex-1" />{sets.map(_temp5)}<div className="w-8 text-center font-semibold text-foreground/60 shrink-0 border-l border-border/60">{labels.livescore.set}</div></div>;
    const t15 = playerOne?.assets?.photo ?? playerOne?.assets?.logo;
    const t16 = playerOne?.country?.code;
    const t17 = playerOne?.country?.flag;
    let t18;
    if ($[37] !== nameOne || $[38] !== t15 || $[39] !== t16 || $[40] !== t17) {
      t18 = <TeamLogo src={t15} alt={nameOne} size="md" kind="player" className="rounded-full" countryCode={t16} countryFlag={t17} />;
      $[37] = nameOne;
      $[38] = t15;
      $[39] = t16;
      $[40] = t17;
      $[41] = t18;
    } else {
      t18 = $[41];
    }
    const t19 = playerOne?.country?.flag;
    let t20;
    if ($[42] !== c1Won || $[43] !== c2Won || $[44] !== nameOne || $[45] !== t19) {
      t20 = <TennisCompetitorDisplay name={nameOne} flag={t19} isWinner={c1Won} isLoser={c2Won} />;
      $[42] = c1Won;
      $[43] = c2Won;
      $[44] = nameOne;
      $[45] = t19;
      $[46] = t20;
    } else {
      t20 = $[46];
    }
    t11 = <div className="flex items-center gap-2 pr-1">{t18}{t20}{isActive && sets.map(s_2 => <div key={s_2.key} className={setScoreClass(s_2.c1, s_2.c2)}>{s_2.c1 ?? ""}</div>)}<div className={isActive ? totalScoreClass(setsWonC1, setsWonC2) : "w-8 text-center text-sm text-muted-foreground shrink-0"}>{isActive ? setsWonC1 : "-"}</div></div>;
    t5 = "flex items-center gap-2 mt-0.5 pr-1";
    const t21 = playerTwo?.assets?.photo ?? playerTwo?.assets?.logo;
    const t22 = playerTwo?.country?.code;
    const t23 = playerTwo?.country?.flag;
    if ($[47] !== nameTwo || $[48] !== t21 || $[49] !== t22 || $[50] !== t23) {
      t6 = <TeamLogo src={t21} alt={nameTwo} size="md" kind="player" className="rounded-full" countryCode={t22} countryFlag={t23} />;
      $[47] = nameTwo;
      $[48] = t21;
      $[49] = t22;
      $[50] = t23;
      $[51] = t6;
    } else {
      t6 = $[51];
    }
    const t24 = playerTwo?.country?.flag;
    if ($[52] !== c1Won || $[53] !== c2Won || $[54] !== nameTwo || $[55] !== t24) {
      t7 = <TennisCompetitorDisplay name={nameTwo} flag={t24} isWinner={c2Won} isLoser={c1Won} />;
      $[52] = c1Won;
      $[53] = c2Won;
      $[54] = nameTwo;
      $[55] = t24;
      $[56] = t7;
    } else {
      t7 = $[56];
    }
    t8 = isActive && sets.map(s_3 => <div key={s_3.key} className={setScoreClass(s_3.c2, s_3.c1)}>{s_3.c2 ?? ""}</div>);
    t4 = isActive ? totalScoreClass(setsWonC2, setsWonC1) : "w-8 text-center text-sm text-muted-foreground shrink-0";
    $[8] = event;
    $[9] = isActive;
    $[10] = isFinished;
    $[11] = labels.livescore.set;
    $[12] = nameOne;
    $[13] = nameTwo;
    $[14] = playerOne?.assets?.logo;
    $[15] = playerOne?.assets?.photo;
    $[16] = playerOne?.country?.code;
    $[17] = playerOne?.country?.flag;
    $[18] = playerTwo?.assets?.logo;
    $[19] = playerTwo?.assets?.photo;
    $[20] = playerTwo?.country?.code;
    $[21] = playerTwo?.country?.flag;
    $[22] = setsWonC2;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
  } else {
    setsWonC2 = $[22];
    t10 = $[23];
    t11 = $[24];
    t12 = $[25];
    t13 = $[26];
    t4 = $[27];
    t5 = $[28];
    t6 = $[29];
    t7 = $[30];
    t8 = $[31];
    t9 = $[32];
  }
  const t14 = isActive ? setsWonC2 : "-";
  let t15;
  if ($[57] !== t14 || $[58] !== t4) {
    t15 = <div className={t4}>{t14}</div>;
    $[57] = t14;
    $[58] = t4;
    $[59] = t15;
  } else {
    t15 = $[59];
  }
  let t16;
  if ($[60] !== t15 || $[61] !== t5 || $[62] !== t6 || $[63] !== t7 || $[64] !== t8) {
    t16 = <div className={t5}>{t6}{t7}{t8}{t15}</div>;
    $[60] = t15;
    $[61] = t5;
    $[62] = t6;
    $[63] = t7;
    $[64] = t8;
    $[65] = t16;
  } else {
    t16 = $[65];
  }
  let t17;
  if ($[66] !== t10 || $[67] !== t11 || $[68] !== t16 || $[69] !== t9) {
    t17 = <div className={t9}>{t10}{t11}{t16}</div>;
    $[66] = t10;
    $[67] = t11;
    $[68] = t16;
    $[69] = t9;
    $[70] = t17;
  } else {
    t17 = $[70];
  }
  let t18;
  if ($[71] !== t12 || $[72] !== t13 || $[73] !== t17) {
    t18 = <div className={t12}>{t13}{t17}</div>;
    $[71] = t12;
    $[72] = t13;
    $[73] = t17;
    $[74] = t18;
  } else {
    t18 = $[74];
  }
  return t18;
}
function _temp5(s_1) {
  return <div key={s_1.key} className="w-8 text-center shrink-0">{s_1.label}</div>;
}
function _temp4(mine_0, theirs_0) {
  return cn("w-8 text-center text-sm font-bold tabular-nums shrink-0 border-l border-border/60", mine_0 > theirs_0 ? "text-foreground bg-foreground/5 rounded-sm" : "text-muted-foreground");
}
function _temp3(mine, theirs) {
  return cn("w-8 text-center text-xs tabular-nums shrink-0", mine != null && theirs != null && mine > theirs ? "font-bold text-foreground bg-foreground/5 rounded-sm" : "text-muted-foreground");
}
function _temp2(s_0) {
  return s_0.c1 !== null && s_0.c2 !== null && s_0.c2 > s_0.c1;
}
function _temp(s) {
  return s.c1 !== null && s.c2 !== null && s.c1 > s.c2;
}